import type { ApiError } from './ApiError';
import { SOURCE_LABELS } from './types';
import type { ErrorCode, Source } from './types';

export interface ErrorCopy {
  /** Short headline. */
  title: string;
  /** What happened, in the user's terms. */
  body: string;
  /** What to do next. Omitted when there is genuinely nothing to do but wait. */
  hint?: string;
  /** Whether offering a "try again" button makes sense for this code. */
  retryable: boolean;
}

/**
 * One human message per documented `error.code` (CONTRACT.md §1).
 *
 * The server's own `message` is deliberately not shown: the contract says it is
 * prose that will change, and it is written for whoever is reading logs. The one
 * exception is `list_too_small`, whose message carries the match count — we
 * append it as a detail rather than replacing our explanation with it.
 */
export function errorCopy(error: ApiError, source: Source, username: string): ErrorCopy {
  const sourceLabel = SOURCE_LABELS[source];
  const who = username.trim() === '' ? 'that account' : `"${username.trim()}"`;

  const copy = COPY[error.code]({ sourceLabel, who });

  if (error.code === 'rate_limited' && error.retryAfterSeconds !== null) {
    return {
      ...copy,
      hint: `Try again in about ${formatSeconds(error.retryAfterSeconds)}.`,
    };
  }

  if (error.code === 'list_too_small' && error.serverMessage) {
    // The count is genuine information the client cannot compute for itself.
    return { ...copy, hint: `${error.serverMessage} ${copy.hint ?? ''}`.trim() };
  }

  return copy;
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

interface CopyContext {
  sourceLabel: string;
  who: string;
}

const COPY: Record<ErrorCode, (ctx: CopyContext) => ErrorCopy> = {
  user_not_found: ({ sourceLabel, who }) => ({
    title: 'No such user',
    body: `${sourceLabel} has no account called ${who}.`,
    hint: 'Check the spelling — usernames are exact, and a display name is not always the same as the account name. If you signed up on the other site, switch the source above.',
    retryable: false,
  }),

  list_private: ({ sourceLabel, who }) => ({
    title: 'That list is private',
    body: `${who} exists on ${sourceLabel}, but the anime list is not public, so there is nothing here to read.`,
    hint:
      sourceLabel === 'AniList'
        ? 'On AniList: Settings → Media → turn off "Anime list is private", then try again. Your username is never stored.'
        : 'On MyAnimeList: Account Settings → Privacy → set the anime list to Public, then try again. Your username is never stored.',
    retryable: true,
  }),

  list_too_small: () => ({
    title: 'Not enough to go on',
    body: 'Fewer than five titles on that list are in the model’s catalogue, which is too little signal to recommend anything honestly.',
    hint: 'The catalogue only covers titles with enough ratings to model, so very new, very obscure, or mostly-unreleased lists fall short. Add a few more completed shows and come back.',
    retryable: true,
  }),

  upstream_error: ({ sourceLabel }) => ({
    title: `${sourceLabel} did not answer`,
    body: `We reached ${sourceLabel} but the reply was missing or unusable, so the list could not be read.`,
    hint: 'This is almost always temporary — their API rate-limits hard. Wait a moment and try again.',
    retryable: true,
  }),

  model_unavailable: () => ({
    title: 'The recommender is offline',
    body: 'The service that does the actual scoring is not responding, so your list was read but nothing could be ranked.',
    hint: 'Nothing you did caused this. Try again shortly.',
    retryable: true,
  }),

  rate_limited: () => ({
    title: 'Too many requests',
    body: 'You have asked for several recommendations in quick succession and hit our per-address limit.',
    hint: 'Give it a minute and try again.',
    retryable: true,
  }),

  network_error: () => ({
    title: 'Could not reach the server',
    body: 'The request never got a reply — usually a dropped connection, or the backend not running.',
    hint: 'Check your connection and try again.',
    retryable: true,
  }),

  unknown_error: () => ({
    title: 'Something went wrong',
    body: 'The server replied with something we could not make sense of.',
    hint: 'Trying again is worth a shot; if it keeps happening the backend is likely misbehaving.',
    retryable: true,
  }),
};
